import { GridPaginationModel } from '@mui/x-data-grid-premium'
import { partialMatchKey, useQuery } from '@tanstack/react-query'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { getPollingGCTime } from '@omnea-app/fe-api-client/queryConfig'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { TasksHttpClient } from '@omnea-app/tasks/tasksHttpClient'
import { taskQueryKeys } from './queryKeys'

const FIVE_MINUTES = 5 * 60_000

const tasksPerBatch = 100

const fetchTasksBatch = async (
  apiClient: TasksHttpClient,
  paginationModel: GridPaginationModel,
  topLevelFilterKey: TasksTopLevelFilterKey | null
) => {
  const { data, error } = await apiClient.POST('/', {
    body: { paginationModel, topLevelFilter: topLevelFilterKey ?? undefined },
  })
  throwIfApiError(error)
  if (!data) {
    throw new Error('No data')
  }
  return data
}

interface Props {
  topLevelFilterKey: TasksTopLevelFilterKey | null
  enabled?: boolean
}

export const useTasks = ({ topLevelFilterKey, enabled = true }: Props) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.list(topLevelFilterKey),
    enabled,
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const firstBatch = await fetchTasksBatch(tasksApiClient, { page: 0, pageSize: tasksPerBatch }, topLevelFilterKey)
      const totalPages = Math.ceil((firstBatch.totalCount ?? 0) / tasksPerBatch)
      if (totalPages < 2) {
        return firstBatch.tasks
      }

      // All pages starting from index 1 (page 2)
      const pagesToLoad = range(1, totalPages)
      const restOfBatches = await Promise.all(
        pagesToLoad.map((page) => fetchTasksBatch(tasksApiClient, { page, pageSize: tasksPerBatch }, topLevelFilterKey))
      )
      const allTasks = [...firstBatch.tasks, ...restOfBatches.flatMap((data) => data.tasks)]

      // remove duplicates between batches just in case
      return uniqBy(allTasks, 'id')
    },
  })
}

type TasksPageProps = {
  topLevelFilterKey: TasksTopLevelFilterKey | null
  paginationModel: GridPaginationModel
  enabled: boolean
}

// One page in the server's order (newest first). Only correct while the grid neither filters nor sorts,
// since the endpoint can do neither.
export const useTasksPage = ({ topLevelFilterKey, paginationModel: { page, pageSize }, enabled }: TasksPageProps) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.page(topLevelFilterKey, page, pageSize),
    enabled,
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    // Keep the previous page on screen while the next one loads, but never another tab's rows.
    placeholderData: (previousData, previousQuery) =>
      previousQuery && partialMatchKey(previousQuery.queryKey, taskQueryKeys.list(topLevelFilterKey)) ? previousData : undefined,
    queryFn: async () => {
      const token = await getToken()
      return fetchTasksBatch(new TasksHttpClient({ authorization: token }), { page, pageSize }, topLevelFilterKey)
    },
  })
}

const MY_TASK_COUNT_FILTER_KEY = 'pending'

export const useGetMyTaskCount = () => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.listCount(MY_TASK_COUNT_FILTER_KEY),
    refetchInterval: FIVE_MINUTES,
    gcTime: getPollingGCTime(FIVE_MINUTES),
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const { data } = await tasksApiClient.POST('/', {
        body: { paginationModel: { page: 0, pageSize: 0 }, topLevelFilter: MY_TASK_COUNT_FILTER_KEY },
      })

      return data?.totalCount ?? 0
    },
  })
}
